import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, ArrowRight, Upload, FolderOpen } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import VideoUploader from '@/components/video/VideoUploader';
import VideoPicker from '@/components/video/VideoPicker';
import JobForm from '@/components/jobs/JobForm';
import { useJobs } from '@/hooks/useJobs';
import { auth } from '@/lib/firebase';
import type { SearchCriteria } from '@/types/job';

type SourceTab = 'existing' | 'upload';

export default function HomePage() {
  const navigate = useNavigate();
  const { createJob, loading } = useJobs();
  const [uploadedPath, setUploadedPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sourceTab, setSourceTab] = useState<SourceTab>('existing');

  const userId = auth.currentUser?.uid;

  const handleVideoSelected = useCallback((filePath: string) => {
    setUploadedPath(filePath);
  }, []);

  const handleAnalyze = useCallback(
    async (criteria: SearchCriteria) => {
      if (!uploadedPath) return;
      setError(null);
      try {
        const { jobId } = await createJob({
          sourceVideoPath: uploadedPath,
          searchCriteria: criteria,
        });
        navigate(`/jobs/${jobId}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start analysis');
      }
    },
    [uploadedPath, createJob, navigate],
  );

  if (!userId) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Please sign in to use Object Replacement.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 p-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Video className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Object Finder</h1>
        </div>
        <p className="text-muted-foreground">Upload a video and find every scene containing a specific object, label, or text.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">1. Select Video</CardTitle>
          <CardDescription>Pick a previously uploaded video or upload a new one</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-1 p-1 bg-muted rounded-lg">
            <button
              onClick={() => setSourceTab('existing')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                sourceTab === 'existing' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <FolderOpen className="h-4 w-4" />
              Previous Uploads
            </button>
            <button
              onClick={() => setSourceTab('upload')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                sourceTab === 'upload' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Upload className="h-4 w-4" />
              Upload New
            </button>
          </div>

          {sourceTab === 'existing' ? (
            <VideoPicker userId={userId} onSelect={handleVideoSelected} />
          ) : (
            <VideoUploader userId={userId} onUploadComplete={handleVideoSelected} />
          )}

          {uploadedPath && (
            <div className="p-2 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700 font-medium">
                Video selected: <span className="font-normal">{uploadedPath.split('/').pop()?.replace(/^\d+_/, '')}</span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {uploadedPath && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">2. Define What to Find</CardTitle>
            <CardDescription>
              Specify what you're looking for — an object, a label, text on screen, or a combination
            </CardDescription>
          </CardHeader>
          <CardContent>
            <JobForm onSubmit={handleAnalyze} loading={loading} />
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="text-center">
        <Button variant="ghost" onClick={() => navigate('/jobs')}>
          View past analyses <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
